import { ref, onMounted, onUnmounted } from 'vue'

const lastActivityTime = ref(Date.now())
const isSystemAwake = ref(true)
const wakeCount = ref(0)
const lastWakeTime = ref(null)
const WAKE_THRESHOLD_MS = 10000

const wakeCallbacks = new Set()

export function useSystemWake() {
  let visibilityHandler = null
  let focusHandler = null
  let heartbeatInterval = null
  let lastHeartbeat = Date.now()

  const detectWake = (source) => {
    const now = Date.now()
    const timeSinceLastActivity = now - lastActivityTime.value

    if (timeSinceLastActivity > WAKE_THRESHOLD_MS) {
      console.log(`[SystemWake] 🔔 System wake detected via ${source}! Gap: ${Math.round(timeSinceLastActivity / 1000)}s`)
      wakeCount.value++
      lastWakeTime.value = new Date()
      isSystemAwake.value = true

      wakeCallbacks.forEach(callback => {
        try {
          callback({
            source,
            gapMs: timeSinceLastActivity,
            gapSeconds: Math.round(timeSinceLastActivity / 1000),
            wakeCount: wakeCount.value,
            timestamp: now
          })
        } catch (err) {
          console.error('[SystemWake] Callback error:', err)
        }
      })
    }

    lastActivityTime.value = now
  }

  const checkHeartbeat = () => {
    const now = Date.now()
    const expectedInterval = 5000
    const timeSinceLastHeartbeat = now - lastHeartbeat

    if (timeSinceLastHeartbeat > expectedInterval + WAKE_THRESHOLD_MS) {
      detectWake('heartbeat')
    }

    lastHeartbeat = now
  }

  const onWake = (callback) => {
    wakeCallbacks.add(callback)
    return () => wakeCallbacks.delete(callback)
  }

  const initListeners = async () => {
    visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        detectWake('visibility')
      }
    }
    document.addEventListener('visibilitychange', visibilityHandler)

    focusHandler = () => {
      detectWake('focus')
    }
    window.addEventListener('focus', focusHandler)

    heartbeatInterval = setInterval(checkHeartbeat, 5000)
    lastHeartbeat = Date.now()

    try {
      const { listen } = await import('@tauri-apps/api/event')

      const unlistenResume = await listen('tauri://resume', () => {
        console.log('[SystemWake] Tauri resume event received')
        detectWake('tauri-resume')
      })

      const unlistenFocus = await listen('tauri://focus', () => {
        detectWake('tauri-focus')
      })

      return () => {
        unlistenResume()
        unlistenFocus()
      }
    } catch (err) {
      console.log('[SystemWake] Tauri events not available, using browser fallback')
      return () => {}
    }
  }

  let tauriCleanup = null

  const init = async () => {
    lastActivityTime.value = Date.now()
    tauriCleanup = await initListeners()
    console.log('[SystemWake] Initialized - monitoring for system wake events')
  }

  const cleanup = () => {
    if (visibilityHandler) {
      document.removeEventListener('visibilitychange', visibilityHandler)
      visibilityHandler = null
    }
    if (focusHandler) {
      window.removeEventListener('focus', focusHandler)
      focusHandler = null
    }
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval)
      heartbeatInterval = null
    }
    if (tauriCleanup) {
      tauriCleanup()
      tauriCleanup = null
    }
  }

  if (typeof onMounted !== 'undefined') {
    try {
      onMounted(() => init())
      onUnmounted(() => cleanup())
    } catch (err) {
      console.warn('[SystemWake] Lifecycle hooks not available')
    }
  }

  return {
    isSystemAwake,
    wakeCount,
    lastWakeTime,
    lastActivityTime,
    onWake,
    init,
    cleanup,
    detectWake
  }
}
